import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import {
  Button as ReactstrapButton,
  Modal,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import { isOmnibeesExternalPromo } from "./utils";

/**
 * Hook that owns the open/close state for the confirm modals
 * (delete / status) used by PromotionsTable.
 *
 * Returns a `state` object with:
 *   - kind:   "delete" | "status" | null
 *   - promo:  the selected promo (or null)
 *   - action: for `kind === "status"`, "disable" | "enable"
 *
 * Plus `open(kind, promo, extras?)` and `close()`.
 */
export function useConfirmModalsState() {
  const [state, setState] = useState({
    kind: null,
    promo: null,
    action: null,
  });

  const open = useCallback((kind, promo, extras = {}) => {
    setState({
      kind,
      promo: promo ?? null,
      action: extras.action ?? null,
    });
  }, []);

  const close = useCallback(() => {
    setState({ kind: null, promo: null, action: null });
  }, []);

  return { state, open, close };
}

function DeleteModal({ promo, onClose, onDelete }) {
  const handleConfirm = () => {
    onDelete(promo);
    onClose();
  };

  return (
    <Modal
      isOpen
      toggle={onClose}
      size="md"
      returnFocusAfterClose={false}
      className="confirm-delete-modal"
    >
      <ModalBody>
        <div className="confirm-delete-content">
          <h3 className="confirm-delete-title">
            Are you sure you want to delete this promo?
          </h3>
          <div className="confirm-delete-text">
            <p>Please confirm you want to delete the promo</p>
            <p className="confirm-delete-promo-name">
              <strong>{promo?.name}</strong>
            </p>
          </div>
        </div>
      </ModalBody>
      <ModalFooter className="confirm-delete-footer">
        <ReactstrapButton
          className="confirm-delete-cancel-btn"
          onClick={onClose}
        >
          Cancel
        </ReactstrapButton>
        <ReactstrapButton
          className="confirm-delete-delete-btn"
          onClick={handleConfirm}
        >
          Delete
        </ReactstrapButton>
      </ModalFooter>
    </Modal>
  );
}

DeleteModal.propTypes = {
  promo: PropTypes.shape({ name: PropTypes.string }),
  onClose: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

function StatusModal({
  promo,
  action,
  selectedProperty,
  onClose,
  onDisable,
  onActivate,
}) {
  const handleConfirm = () => {
    if (action === "disable") {
      onDisable(promo);
    } else {
      onActivate(promo);
    }
    onClose();
  };

  const isOmnibeesPromo =
    action === "disable" && isOmnibeesExternalPromo(promo, selectedProperty);

  return (
    <Modal
      isOpen
      toggle={onClose}
      centered
      returnFocusAfterClose={false}
      className="confirm-status-modal"
    >
      <div className="confirm-status-modal-content">
        <h3 className="confirm-status-modal-title">
          Are you sure you want to {action} this promo?
        </h3>
        {isOmnibeesPromo ? (
          <p className="confirm-status-modal-message">
            As this promo originated in Omnibees, if you proceed, this
            offer/promo will immediately stop selling in Hotel Trader. This
            offer/promo cannot be reactivated from the Hotel Trader Supplier
            Portal.
          </p>
        ) : (
          <p className="confirm-status-modal-message">
            Please confirm you want {action} the promo
            <span className="confirm-status-modal-name">{promo?.name}</span>
          </p>
        )}
        <div className="confirm-status-modal-actions">
          <button
            type="button"
            className="confirm-status-modal-btn confirm-status-modal-btn-cancel"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="confirm-status-modal-btn confirm-status-modal-btn-primary"
            onClick={handleConfirm}
          >
            Yes, {action}
          </button>
        </div>
      </div>
    </Modal>
  );
}

StatusModal.propTypes = {
  promo: PropTypes.shape({
    name: PropTypes.string,
    htPromo: PropTypes.bool,
  }),
  action: PropTypes.oneOf(["disable", "enable"]),
  selectedProperty: PropTypes.shape({
    crsId: PropTypes.number,
  }),
  onClose: PropTypes.func.isRequired,
  onDisable: PropTypes.func.isRequired,
  onActivate: PropTypes.func.isRequired,
};

function PromotionsConfirmModals({
  state,
  onClose,
  selectedProperty,
  onDisable,
  onActivate,
  onDelete,
}) {
  if (!state?.kind) return null;

  if (state.kind === "delete") {
    return (
      <DeleteModal promo={state.promo} onClose={onClose} onDelete={onDelete} />
    );
  }

  if (state.kind === "status") {
    return (
      <StatusModal
        promo={state.promo}
        action={state.action}
        selectedProperty={selectedProperty}
        onClose={onClose}
        onDisable={onDisable}
        onActivate={onActivate}
      />
    );
  }

  return null;
}

PromotionsConfirmModals.propTypes = {
  state: PropTypes.shape({
    kind: PropTypes.oneOf(["delete", "status", null]),
    promo: PropTypes.object,
    action: PropTypes.oneOf(["disable", "enable", null]),
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  selectedProperty: PropTypes.shape({
    crsId: PropTypes.number,
  }),
  onDisable: PropTypes.func.isRequired,
  onActivate: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

export default PromotionsConfirmModals;
